import React from "react";
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	Divider,
	Grid2,
	Paper,
	Skeleton
} from "@mui/material";

const FreelancerDetailsSkeleton: React.FC = () => {
	return (
		<Grid2 container spacing={3}>
			{/* Header Section Skeleton */}
			<Grid2
				size={12}
				display="flex"
				flexDirection="column"
				alignItems="center"
				sx={{
					marginX: "12px",
					borderRadius: "8px"
				}}
			>
				{/* Top row with location, price, language */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 4, px: 4, mb: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Skeleton variant="circular" width={20} height={20} />
						<Skeleton variant="text" width={120} height={20} />
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Skeleton variant="circular" width={20} height={20} />
						<Skeleton variant="text" width={80} height={20} />
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Skeleton variant="circular" width={20} height={20} />
						<Skeleton variant="text" width={100} height={20} />
					</Box>
				</Box>

				{/* Bottom row with contact and icons */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 4 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Skeleton variant="circular" width={20} height={20} />
						<Skeleton variant="text" width={90} height={20} />
						<Skeleton variant="circular" width={20} height={20} />
					</Box>
					<Skeleton variant="rectangular" width={1} height={16} />
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Skeleton variant="circular" width={20} height={20} />
						<Skeleton variant="text" width={40} height={20} />
						<Skeleton variant="circular" width={20} height={20} />
					</Box>
					<Skeleton variant="rectangular" width={1} height={16} />
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Skeleton variant="circular" width={30} height={30} />
						<Skeleton variant="circular" width={30} height={30} />
						<Skeleton variant="circular" width={30} height={30} />
						<Skeleton variant="circular" width={30} height={30} />
					</Box>
				</Box>
			</Grid2>

			{/* Main Content Skeleton */}
			<Grid2 size={12}>
				{/* Presentation Card Skeleton */}
				<Card
					component={Paper}
					elevation={0}
					sx={{
						margin: "32px",
						borderRadius: "8px"
					}}
				>
					<CardHeader title={<Skeleton variant="text" width={200} height={32} />} />
					<Divider />
					<CardContent sx={{ p: 2 }}>
						<Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
						<Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
						<Skeleton variant="text" width="85%" height={20} sx={{ mb: 1 }} />
						<Skeleton variant="text" width="60%" height={20} />
					</CardContent>
				</Card>

				{/* Chips Section Skeletons */}
				{[1, 2, 3, 4, 5].map(section => (
					<Card
						key={section}
						component={Paper}
						elevation={0}
						sx={{
							margin: "32px",
							borderRadius: "8px"
						}}
					>
						<CardHeader title={<Skeleton variant="text" width={150} height={32} />} />
						<Divider />
						<CardContent sx={{ p: 2 }}>
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{[1, 2, 3, 4, 5].map((chip, index) => {
									const widths = [85, 95, 110, 90, 120]; // Predefined varied widths
									return (
										<Skeleton
											key={chip}
											variant="rounded"
											width={widths[index % widths.length]}
											height={32}
											sx={{ borderRadius: "16px" }}
										/>
									);
								})}
							</Box>
						</CardContent>
					</Card>
				))}

				{/* Experience Section Skeleton */}
				<Card
					component={Paper}
					elevation={0}
					sx={{
						margin: "32px",
						borderRadius: "8px"
					}}
				>
					<CardHeader title={<Skeleton variant="text" width={200} height={32} />} />
					<Divider />
					<CardContent sx={{ p: 2 }}>
						{[1, 2, 3].map(experience => (
							<Box key={experience} display="flex" gap={2} mb={3}>
								{/* Company Logo Skeleton */}
								<Skeleton
									variant="rounded"
									width={48}
									height={48}
									sx={{ borderRadius: "6px", flexShrink: 0 }}
								/>

								{/* Main Content Skeleton */}
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Grid2 container spacing={2} alignItems="flex-start">
										<Grid2 size={8}>
											<Skeleton variant="text" width={200} height={24} sx={{ mb: 0.5 }} />
											<Skeleton variant="text" width={150} height={20} sx={{ mb: 0.5 }} />
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
											>
												<Skeleton variant="text" width={60} height={20} />
												<Skeleton
													variant="rounded"
													width={80}
													height={24}
													sx={{ borderRadius: "12px" }}
												/>
											</Box>
										</Grid2>
										<Grid2 size={4}>
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "flex-end",
													gap: 0.5
												}}
											>
												<Skeleton variant="text" width={100} height={20} />
												<Skeleton variant="text" width={80} height={20} />
												<Skeleton variant="text" width={60} height={20} />
											</Box>
										</Grid2>
									</Grid2>

									{/* Description Skeleton */}
									<Box sx={{ mt: 3 }}>
										<Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
										<Skeleton variant="text" width="90%" height={20} />
									</Box>
								</Box>
							</Box>
						))}
					</CardContent>
				</Card>
			</Grid2>
		</Grid2>
	);
};

export default FreelancerDetailsSkeleton;
